import { Container, Graphics, Text, type TextStyle } from 'pixi.js';
import type { Season } from './net.js';

const MONO: Partial<TextStyle> = {
  fontFamily: 'monospace',
  fontSize: 13,
  fill: 0xffffff,
};

// Dark, near-opaque chip behind every HUD text so it stays WCAG AA-readable
// (>=4.5:1) no matter what's rendered underneath — water, sand, snow, etc.
const CHIP_BG = 0x0a1418;
const CHIP_ALPHA = 0.78;

const SEASON_ICON: Record<Season, string> = {
  vår: '🌸', sommar: '☀️', höst: '🍂', vinter: '❄️',
};

// Accent border color per season — purely decorative, doesn't carry the
// contrast requirement (the white label text on CHIP_BG does that).
const SEASON_ACCENT: Record<Season, number> = {
  vår: 0xff9ecf, sommar: 0xffd23f, höst: 0xff8c42, vinter: 0x8ecbff,
};

export class Hud {
  container: Container;
  private scoreChip:    Graphics;
  private scoreText:    Text;
  private seasonChip:   Graphics;
  private seasonText:   Text;

  constructor() {
    this.container = new Container();

    this.scoreChip    = new Graphics();
    this.scoreText    = new Text({ text: '', style: { ...MONO, fontSize: 24, fill: 0xffd700 } });
    this.seasonChip   = new Graphics();
    this.seasonText   = new Text({ text: '', style: { ...MONO, fontSize: 20, fill: 0xffffff } });

    this.container.addChild(this.scoreChip);
    this.container.addChild(this.scoreText);
    this.container.addChild(this.seasonChip);
    this.container.addChild(this.seasonText);
  }

  update(
    screenW: number,
    screenH: number,
    score: number = 0,
    season: Season | null = null,
  ): void {
    // Season badge — centered top, a single icon in a color-accented circle
    // so the current season is unmistakable at a glance.
    const sg = this.seasonChip;
    sg.clear();
    if (season) {
      this.seasonText.text = SEASON_ICON[season];
      const r = 21;
      const cx = screenW / 2;
      const cy = 10 + r;
      sg.circle(cx, cy, r);
      sg.fill({ color: CHIP_BG, alpha: CHIP_ALPHA });
      sg.circle(cx, cy, r);
      sg.stroke({ color: SEASON_ACCENT[season], width: 2, alpha: 0.9 });
      this.seasonText.x = cx - this.seasonText.width / 2;
      this.seasonText.y = cy - this.seasonText.height / 2;
    } else {
      this.seasonText.text = '';
    }

    // Treasure score (bottom-left)
    const bg = this.scoreChip;
    bg.clear();
    if (score > 0) {
      this.scoreText.text = `\u{1F4B0} ${score}`;
      const sPadX = 10, sPadY = 5;
      const sw = this.scoreText.width + sPadX * 2;
      const sh = this.scoreText.height + sPadY * 2;
      const sx = 14;
      const sy = screenH - sh - 14;
      bg.roundRect(sx, sy, sw, sh, 8);
      bg.fill({ color: CHIP_BG, alpha: CHIP_ALPHA });
      this.scoreText.x = sx + sPadX;
      this.scoreText.y = sy + sPadY;
    } else {
      this.scoreText.text = '';
    }
  }
}
